import { useEffect, useState, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import api from "../../services/api";

const C = {
  sage: "#4A7C59", sageL: "#EAF4EE", sageD: "#2D5A3D",
  saffron: "#E8922A", saffronL: "#FDF0D5", saffronD: "#B86A10",
  cream: "#FDFAF3", ink: "#2C1810", inkMid: "#5C3D2E", inkLight: "#9A7B60",
  turmeric: "#F7C948", terra: "#B85C38",
};

const BLANK = {
  name: "", qualification: "", experience: "", expertise: "",
  specialization: "", bio: "", timing: "", consultFee: "",
  insights: "", insightsTitle: "", isVisible: true,
};

const BACKEND =
  window.location.hostname === "localhost"
    ? "http://localhost:5000"
    : "https://herambaayurveda.in";

/* ── Small helpers ── */
function Label({ children }) {
  return (
    <label style={{ display: "block", fontSize: 12, fontWeight: 700,
      color: C.inkLight, textTransform: "uppercase", letterSpacing: "0.08em",
      marginBottom: 5, fontFamily: "'Inter',sans-serif" }}>
      {children}
    </label>
  );
}

function Input({ value, onChange, placeholder, type = "text" }) {
  return (
    <input
      type={type}
      value={value}
      onChange={e => onChange(e.target.value)}
      placeholder={placeholder}
      autoComplete="off"
      style={{
        width: "100%", border: `1.5px solid ${C.saffronL}`,
        borderRadius: 10, padding: "10px 14px", fontSize: 14,
        color: C.ink, background: "#fff", outline: "none",
        fontFamily: "'Inter',sans-serif", boxSizing: "border-box",
        transition: "border 0.2s",
      }}
      onFocus={e => e.target.style.borderColor = C.saffron}
      onBlur={e => e.target.style.borderColor = C.saffronL}
    />
  );
}

function Textarea({ value, onChange, placeholder, rows = 3 }) {
  return (
    <textarea
      value={value}
      onChange={e => onChange(e.target.value)}
      placeholder={placeholder}
      rows={rows}
      style={{
        width: "100%", border: `1.5px solid ${C.saffronL}`,
        borderRadius: 10, padding: "10px 14px", fontSize: 14,
        color: C.ink, background: "#fff", outline: "none", resize: "vertical",
        fontFamily: "'Inter',sans-serif", boxSizing: "border-box",
        transition: "border 0.2s",
      }}
      onFocus={e => e.target.style.borderColor = C.saffron}
      onBlur={e => e.target.style.borderColor = C.saffronL}
    />
  );
}

function Toggle({ value, onChange, label }) {
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 10, cursor: "pointer" }}
      onClick={() => onChange(!value)}>
      <div style={{
        width: 44, height: 24, borderRadius: 12, position: "relative",
        background: value ? C.sage : "#D1D5DB",
        transition: "background 0.25s",
      }}>
        <motion.div
          animate={{ x: value ? 22 : 2 }}
          transition={{ type: "spring", stiffness: 400, damping: 30 }}
          style={{
            position: "absolute", top: 2, width: 20, height: 20,
            borderRadius: "50%", background: "#fff",
            boxShadow: "0 1px 4px rgba(0,0,0,0.2)",
          }}
        />
      </div>
      <span style={{ fontSize: 13, color: C.inkMid, fontFamily: "'Inter',sans-serif", fontWeight: 600 }}>
        {label}
      </span>
    </div>
  );
}

/* ── Toast ── */
function Toast({ msg, type }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: -30, scale: 0.9 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      exit={{ opacity: 0, y: -20, scale: 0.9 }}
      style={{
        position: "fixed", top: 20, right: 20, zIndex: 9999,
        background: type === "error" ? "#EF4444" : C.sage,
        color: "#fff", padding: "12px 22px", borderRadius: 12,
        fontSize: 14, fontWeight: 600, fontFamily: "'Inter',sans-serif",
        boxShadow: "0 8px 30px rgba(0,0,0,0.15)",
      }}>
      {msg}
    </motion.div>
  );
}

/* ── Doctor card preview (admin list) ── */
function DoctorListCard({ doc, onEdit, onDelete, onToggle, deleting }) {
  const photoUrl = doc.photo
    ? (doc.photo.startsWith("http") ? doc.photo : `${BACKEND}${doc.photo}`)
    : null;

  return (
    <motion.div
      layout
      initial={{ opacity: 0, y: 16 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, scale: 0.95 }}
      style={{
        background: "#fff", borderRadius: 18,
        border: `1.5px solid ${doc.isVisible !== false ? C.saffronL : "#E5E7EB"}`,
        padding: "18px 22px", display: "flex", alignItems: "center",
        gap: 18, boxShadow: "0 2px 16px rgba(74,124,89,0.06)",
      }}>
      {/* Photo */}
      <div style={{
        width: 64, height: 64, borderRadius: "50%", flexShrink: 0,
        overflow: "hidden", background: `linear-gradient(135deg,${C.turmeric},${C.saffron})`,
        display: "flex", alignItems: "center", justifyContent: "center",
        border: `2px solid ${C.saffronL}`,
      }}>
        {photoUrl
          ? <img src={photoUrl} alt={doc.name} style={{ width: "100%", height: "100%", objectFit: "cover" }} />
          : <span style={{ fontSize: 26 }}>🩺</span>}
      </div>

      {/* Info */}
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
          <span style={{ fontFamily: "'Playfair Display',serif", fontWeight: 800, fontSize: 16, color: C.ink }}>
            Dr. {doc.name}
          </span>
          <span style={{
            fontSize: 11, fontWeight: 700, padding: "2px 10px", borderRadius: 20,
            background: doc.isVisible !== false ? C.sageL : "#F3F4F6",
            color: doc.isVisible !== false ? C.sageD : "#6B7280",
          }}>
            {doc.isVisible !== false ? "● Visible" : "○ Hidden"}
          </span>
        </div>
        <p style={{ fontSize: 13, color: C.inkLight, marginTop: 3, fontFamily: "'Inter',sans-serif" }}>
          {doc.qualification || "—"}{doc.experience ? ` · ${doc.experience}+ yrs` : ""}
          {doc.expertise ? ` · ${doc.expertise}` : ""}
        </p>
      </div>

      {/* Actions */}
      <div style={{ display: "flex", gap: 8, flexShrink: 0 }}>
        <motion.button whileHover={{ scale: 1.07 }} whileTap={{ scale: 0.95 }}
          onClick={() => onToggle(doc)}
          title={doc.isVisible !== false ? "Hide from public" : "Show on public site"}
          style={{
            background: doc.isVisible !== false ? C.sageL : "#F3F4F6",
            border: "none", borderRadius: 10, padding: "8px 12px",
            cursor: "pointer", fontSize: 16,
          }}>
          {doc.isVisible !== false ? "👁️" : "🙈"}
        </motion.button>
        <motion.button whileHover={{ scale: 1.07 }} whileTap={{ scale: 0.95 }}
          onClick={() => onEdit(doc)}
          style={{
            background: "#EFF6FF", color: "#2563EB", border: "none",
            borderRadius: 10, padding: "8px 16px", fontSize: 13,
            fontWeight: 700, cursor: "pointer", fontFamily: "'Inter',sans-serif",
          }}>
          Edit
        </motion.button>
        <motion.button whileHover={{ scale: 1.07 }} whileTap={{ scale: 0.95 }}
          onClick={() => onDelete(doc._id)}
          disabled={deleting === doc._id}
          style={{
            background: "#FEF2F2", color: "#DC2626", border: "none",
            borderRadius: 10, padding: "8px 16px", fontSize: 13,
            fontWeight: 700, cursor: "pointer", fontFamily: "'Inter',sans-serif",
            opacity: deleting === doc._id ? 0.5 : 1,
          }}>
          {deleting === doc._id ? "..." : "Delete"}
        </motion.button>
      </div>
    </motion.div>
  );
}

/* ══════════════════════════════════════════════
   MAIN COMPONENT
═══════════════════════════════════════════════ */
export default function AdminDoctorProfiles() {
  const [doctors, setDoctors]   = useState([]);
  const [loading, setLoading]   = useState(true);
  const [form, setForm]         = useState(BLANK);
  const [editId, setEditId]     = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [saving, setSaving]     = useState(false);
  const [deleting, setDeleting] = useState(null);
  const [photoFile, setPhotoFile]     = useState(null);
  const [photoPreview, setPhotoPreview] = useState(null);
  const [uploading, setUploading]     = useState(false);
  const [toast, setToast]       = useState(null);
  const fileRef  = useRef();
  const formRef  = useRef();

  const notify = (msg, type = "success") => {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 3200);
  };

  const load = async () => {
    try {
      const res = await api.get("/doctor-profiles?all=true");
      setDoctors(res.data?.data || res.data || []);
    } catch { setDoctors([]); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const setF = key => val => setForm(prev => ({ ...prev, [key]: val }));

  const openNew = () => {
    setForm(BLANK);
    setEditId(null);
    setPhotoFile(null);
    setPhotoPreview(null);
    setShowForm(true);
    setTimeout(() => formRef.current?.scrollIntoView({ behavior: "smooth" }), 80);
  };

  const openEdit = doc => {
    setForm({
      name: doc.name || "", qualification: doc.qualification || "",
      experience: doc.experience || "", expertise: doc.expertise || "",
      specialization: doc.specialization || "", bio: doc.bio || "",
      timing: doc.timing || "", consultFee: doc.consultFee || "",
      insights: doc.insights || "", insightsTitle: doc.insightsTitle || "",
      isVisible: doc.isVisible !== false,
    });
    setPhotoFile(null);
    const p = doc.photo
      ? (doc.photo.startsWith("http") ? doc.photo : `${BACKEND}${doc.photo}`)
      : null;
    setPhotoPreview(p);
    setEditId(doc._id);
    setShowForm(true);
    setTimeout(() => formRef.current?.scrollIntoView({ behavior: "smooth" }), 80);
  };

  const handlePhotoSelect = e => {
    const f = e.target.files[0];
    if (!f) return;
    setPhotoFile(f);
    setPhotoPreview(URL.createObjectURL(f));
  };

  const save = async () => {
    if (!form.name.trim()) { notify("Doctor name is required", "error"); return; }
    setSaving(true);
    try {
      let photoPath = form.photo || null;

      if (photoFile) {
        setUploading(true);
        const fd = new FormData();
        fd.append("file", photoFile);
        const upRes = await api.post("/upload", fd, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        photoPath = upRes.data?.url || upRes.data?.path || null;
        setUploading(false);
      }

      const payload = { ...form, photo: photoPath };

      if (editId) {
        await api.put(`/doctor-profiles/${editId}`, payload);
        notify("Doctor profile updated ✓");
      } else {
        await api.post("/doctor-profiles", payload);
        notify("Doctor profile added ✓");
      }
      setShowForm(false);
      load();
    } catch (err) {
      notify(err?.response?.data?.message || "Save failed", "error");
    } finally { setSaving(false); setUploading(false); }
  };

  const del = async id => {
    if (!window.confirm("Delete this doctor profile? This cannot be undone.")) return;
    setDeleting(id);
    try {
      await api.delete(`/doctor-profiles/${id}`);
      notify("Profile deleted.");
      if (editId === id) { setEditId(null); setShowForm(false); setForm(BLANK); }
      load();
    } catch { notify("Delete failed", "error"); }
    finally { setDeleting(null); }
  };

  const toggle = async doc => {
    try {
      await api.put(`/doctor-profiles/${doc._id}`, { ...doc, isVisible: !doc.isVisible });
      load();
    } catch { notify("Update failed", "error"); }
  };

  /* ── RENDER ── */
  return (
    <div style={{ padding: "28px 24px", maxWidth: 900, margin: "0 auto" }}>

      <AnimatePresence>
        {toast && <Toast key="toast" msg={toast.msg} type={toast.type} />}
      </AnimatePresence>

      {/* ── Header ── */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 28 }}>
        <div>
          <h1 style={{ fontFamily: "'Playfair Display',serif", fontSize: 28, fontWeight: 800,
            color: C.ink, margin: 0 }}>Doctor Profiles</h1>
          <p style={{ fontSize: 14, color: C.inkLight, marginTop: 5, fontFamily: "'Inter',sans-serif" }}>
            Add doctors with photo, qualifications, expertise & health insights — shown as flip cards on the public site.
          </p>
        </div>
        {!showForm && (
          <motion.button
            whileHover={{ scale: 1.04, boxShadow: `0 8px 24px rgba(74,124,89,0.3)` }}
            whileTap={{ scale: 0.97 }}
            onClick={openNew}
            style={{
              background: `linear-gradient(135deg,${C.sage},${C.sageD})`,
              color: "#fff", border: "none", borderRadius: 12,
              padding: "11px 22px", fontSize: 14, fontWeight: 700,
              cursor: "pointer", fontFamily: "'Inter',sans-serif",
              display: "flex", alignItems: "center", gap: 7,
              flexShrink: 0,
            }}>
            + Add Doctor
          </motion.button>
        )}
      </div>

      {/* ── FORM ── */}
      <AnimatePresence>
        {showForm && (
          <motion.div
            ref={formRef}
            key="form"
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -16 }}
            transition={{ duration: 0.3 }}
            style={{
              background: "#fff", borderRadius: 22,
              border: `2px solid ${C.saffronL}`,
              padding: "30px 28px", marginBottom: 32,
              boxShadow: "0 8px 40px rgba(232,146,42,0.08)",
            }}>
            <h2 style={{ fontFamily: "'Playfair Display',serif", fontSize: 20, fontWeight: 800,
              color: C.ink, marginTop: 0, marginBottom: 24 }}>
              {editId ? "✏️ Edit Doctor Profile" : "👨‍⚕️ New Doctor Profile"}
            </h2>

            {/* ── Photo Upload ── */}
            <div style={{ display: "flex", gap: 20, alignItems: "flex-start", marginBottom: 28 }}>
              <div
                onClick={() => fileRef.current?.click()}
                style={{
                  width: 110, height: 110, borderRadius: "50%", flexShrink: 0,
                  border: `2.5px dashed ${C.saffron}`, cursor: "pointer",
                  overflow: "hidden", background: C.saffronL,
                  display: "flex", alignItems: "center", justifyContent: "center",
                  transition: "border-color 0.2s",
                  position: "relative",
                }}>
                {photoPreview
                  ? <img src={photoPreview} alt="Preview" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                  : (
                    <div style={{ textAlign: "center" }}>
                      <div style={{ fontSize: 28 }}>📷</div>
                      <div style={{ fontSize: 10, color: C.saffronD, fontWeight: 700, marginTop: 3,
                        fontFamily: "'Inter',sans-serif" }}>UPLOAD</div>
                    </div>
                  )}
              </div>
              <div style={{ paddingTop: 8 }}>
                <p style={{ fontWeight: 700, color: C.ink, fontSize: 14, marginBottom: 6,
                  fontFamily: "'Inter',sans-serif" }}>Doctor Photo</p>
                <p style={{ fontSize: 12, color: C.inkLight, marginBottom: 12,
                  fontFamily: "'Inter',sans-serif", lineHeight: 1.6 }}>
                  Upload a professional headshot.<br/>
                  JPG or PNG · Square recommended · Max 5 MB
                </p>
                <motion.button
                  whileHover={{ scale: 1.03 }} whileTap={{ scale: 0.97 }}
                  onClick={() => fileRef.current?.click()}
                  style={{
                    background: C.saffronL, border: `1.5px solid ${C.saffron}50`,
                    borderRadius: 10, padding: "7px 16px", fontSize: 13,
                    fontWeight: 700, cursor: "pointer", color: C.saffronD,
                    fontFamily: "'Inter',sans-serif",
                  }}>
                  {photoPreview ? "Change Photo" : "Choose Photo"}
                </motion.button>
                {photoPreview && (
                  <button
                    onClick={() => { setPhotoFile(null); setPhotoPreview(null); setF("photo")(null); }}
                    style={{ background: "none", border: "none", fontSize: 12,
                      color: "#EF4444", cursor: "pointer", marginLeft: 10,
                      fontFamily: "'Inter',sans-serif" }}>
                    Remove
                  </button>
                )}
              </div>
              <input ref={fileRef} type="file" accept="image/*" style={{ display: "none" }}
                onChange={handlePhotoSelect} />
            </div>

            {/* ── Fields Grid ── */}
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(min(220px,100%),1fr))", gap: "18px 20px" }}>

              {/* Full-width name */}
              <div style={{ gridColumn: "1 / -1" }}>
                <Label>Doctor Full Name *</Label>
                <Input value={form.name} onChange={setF("name")} placeholder='e.g. Priya Sharma' />
              </div>

              <div>
                <Label>Qualification</Label>
                <Input value={form.qualification} onChange={setF("qualification")} placeholder='e.g. BAMS, MD (Ayurveda)' />
              </div>

              <div>
                <Label>Years of Experience</Label>
                <Input value={form.experience} onChange={setF("experience")} placeholder='e.g. 12' />
              </div>

              <div style={{ gridColumn: "1 / -1" }}>
                <Label>Area of Expertise</Label>
                <Input value={form.expertise} onChange={setF("expertise")}
                  placeholder='e.g. Panchakarma, Women Health, Joint Disorders' />
              </div>

              <div style={{ gridColumn: "1 / -1" }}>
                <Label>Specialization (detailed)</Label>
                <Textarea value={form.specialization} onChange={setF("specialization")}
                  placeholder="Describe the doctor's specializations in detail..." rows={2} />
              </div>

              <div>
                <Label>Clinic Timing</Label>
                <Input value={form.timing} onChange={setF("timing")} placeholder='e.g. 9:00 AM – 6:00 PM' />
              </div>

              <div>
                <Label>Consultation Fee</Label>
                <Input value={form.consultFee} onChange={setF("consultFee")} placeholder='e.g. ₹500' />
              </div>

              <div style={{ gridColumn: "1 / -1" }}>
                <Label>Doctor Bio</Label>
                <Textarea value={form.bio} onChange={setF("bio")} rows={3}
                  placeholder='A brief professional biography visible on the front of the card...' />
              </div>

              {/* Insights section */}
              <div style={{
                gridColumn: "1 / -1",
                background: C.sageL, borderRadius: 14, padding: "18px 20px",
                border: `1.5px solid ${C.sage}30`,
              }}>
                <p style={{ fontFamily: "'Playfair Display',serif", fontWeight: 800,
                  fontSize: 15, color: C.sageD, marginBottom: 4 }}>
                  🔄 Card Flip — Doctor's Health Insight
                </p>
                <p style={{ fontSize: 12, color: C.inkLight, marginBottom: 14,
                  fontFamily: "'Inter',sans-serif" }}>
                  When a visitor flips the card, they'll see this doctor's personal insight on a health topic.
                </p>
                <Label>Insight Topic / Title</Label>
                <div style={{ marginBottom: 12 }}>
                  <Input value={form.insightsTitle} onChange={setF("insightsTitle")}
                    placeholder="e.g. On Seasonal Detox or Dr. Sharma's Tip for Joint Health" />
                </div>
                <Label>Insight Content</Label>
                <Textarea value={form.insights} onChange={setF("insights")} rows={4}
                  placeholder="The doctor's advice or tip shown on the back of the card when flipped..." />
              </div>

              {/* Visibility toggle */}
              <div style={{ gridColumn: "1 / -1" }}>
                <Toggle value={form.isVisible} onChange={setF("isVisible")}
                  label={form.isVisible ? "Visible on public website" : "Hidden from public website"} />
              </div>
            </div>

            {/* ── Form Actions ── */}
            <div style={{ display: "flex", gap: 12, marginTop: 26 }}>
              <motion.button
                whileHover={{ scale: 1.03 }} whileTap={{ scale: 0.97 }}
                onClick={save}
                disabled={saving}
                style={{
                  background: `linear-gradient(135deg,${C.sage},${C.sageD})`,
                  color: "#fff", border: "none", borderRadius: 12,
                  padding: "12px 28px", fontSize: 14, fontWeight: 700,
                  cursor: saving ? "not-allowed" : "pointer",
                  fontFamily: "'Inter',sans-serif", opacity: saving ? 0.7 : 1,
                }}>
                {saving ? (uploading ? "Uploading photo..." : "Saving...") : (editId ? "Update Profile" : "Add Profile")}
              </motion.button>
              <motion.button
                whileHover={{ scale: 1.03 }} whileTap={{ scale: 0.97 }}
                onClick={() => setShowForm(false)}
                style={{
                  background: "#F9FAFB", color: C.inkMid,
                  border: `1.5px solid #E5E7EB`, borderRadius: 12,
                  padding: "12px 22px", fontSize: 14, fontWeight: 600,
                  cursor: "pointer", fontFamily: "'Inter',sans-serif",
                }}>
                Cancel
              </motion.button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* ── Doctor List ── */}
      {loading ? (
        <div style={{ textAlign: "center", padding: "80px 0", color: C.inkLight,
          fontFamily: "'Inter',sans-serif" }}>
          <motion.div animate={{ rotate: 360 }}
            transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
            style={{ fontSize: 36, display: "inline-block", marginBottom: 12 }}>🌿</motion.div>
          <p>Loading profiles...</p>
        </div>
      ) : doctors.length === 0 ? (
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}
          style={{
            textAlign: "center", padding: "72px 24px",
            background: "#fff", borderRadius: 22, border: `2px dashed ${C.saffronL}`,
          }}>
          <div style={{ fontSize: 56, marginBottom: 14 }}>🩺</div>
          <h3 style={{ fontFamily: "'Playfair Display',serif", fontSize: 20, color: C.ink, marginBottom: 8 }}>
            No Doctor Profiles Yet
          </h3>
          <p style={{ fontSize: 14, color: C.inkLight, marginBottom: 22,
            fontFamily: "'Inter',sans-serif" }}>
            Add your first doctor to display them on the public "Our Doctors" page.
          </p>
          <motion.button
            whileHover={{ scale: 1.04 }} whileTap={{ scale: 0.97 }}
            onClick={openNew}
            style={{
              background: `linear-gradient(135deg,${C.sage},${C.sageD})`,
              color: "#fff", border: "none", borderRadius: 12,
              padding: "12px 26px", fontSize: 14, fontWeight: 700,
              cursor: "pointer", fontFamily: "'Inter',sans-serif",
            }}>
            + Add First Doctor
          </motion.button>
        </motion.div>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
          <p style={{ fontSize: 12, color: C.inkLight, fontFamily: "'Inter',sans-serif",
            marginBottom: 4 }}>
            {doctors.length} profile{doctors.length !== 1 ? "s" : ""} · Click <strong>Edit</strong> to modify · Toggle 👁️ to show/hide
          </p>
          <AnimatePresence>
            {doctors.map(doc => (
              <DoctorListCard key={doc._id} doc={doc}
                onEdit={openEdit} onDelete={del}
                onToggle={toggle} deleting={deleting} />
            ))}
          </AnimatePresence>
        </div>
      )}
    </div>
  );
}